import React, { useEffect, useState } from 'react';
import { vault as vaultApi } from '@/utils/api';
import { Plus, Eye, EyeOff, Copy, Trash2, X, Key, Lock, Globe, Shield } from 'lucide-react';
import { toast } from 'sonner';

const TYPE_ICONS = { credential: Lock, api_key: Key, secret: Shield, ssh_key: Key, oauth_token: Key };
const TYPE_LABELS = { credential: 'Credential', api_key: 'API Key', secret: 'Secret', ssh_key: 'SSH Key', oauth_token: 'OAuth Token' };

function VaultEntry({ entry, onReveal, onDelete, isOperator }) {
  const [revealed, setRevealed] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const [timer, setTimer] = useState(null);
  const Icon = TYPE_ICONS[entry.type] || Lock;

  const handleReveal = async () => {
    if (revealed) {
      setRevealed(null);
      if (timer) clearTimeout(timer);
      return;
    }
    setRevealing(true);
    try {
      const data = await onReveal(entry.id);
      setRevealed(data);
      // Auto-hide after 30s
      const t = setTimeout(() => setRevealed(null), 30000);
      setTimer(t);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reveal');
    } finally { setRevealing(false); }
  };

  const copyToClipboard = (value, label) => {
    navigator.clipboard.writeText(value).then(() => {
      toast.success(`${label} copied to clipboard`);
      // Clear after 30s
      setTimeout(() => {
        navigator.clipboard.writeText('').catch(() => {});
      }, 30000);
    }).catch(() => toast.error('Failed to copy'));
  };

  return (
    <div className="card-base p-4" data-testid={`vault-entry-${entry.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-btn flex items-center justify-center flex-shrink-0" style={{ background: '#E6F1F4' }}>
            <Icon size={15} style={{ color: '#006080' }} />
          </div>
          <div>
            <p className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>{entry.name}</p>
            <p className="text-xs" style={{ color: 'var(--muted)' }}>{TYPE_LABELS[entry.type] || entry.type}</p>
            {entry.url && (
              <a href={entry.url} target="_blank" rel="noreferrer" className="text-xs flex items-center gap-1 mt-0.5" style={{ color: '#006080' }}>
                <Globe size={10} />{entry.url}
              </a>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="text-xs px-2.5 py-1 rounded-btn transition-colors flex items-center gap-1"
            style={{ background: '#E6F1F4', color: '#006080' }}
            onClick={handleReveal}
            disabled={revealing}
            data-testid={`vault-reveal-${entry.id}`}
          >
            {revealed ? <EyeOff size={12} /> : <Eye size={12} />}
            {revealing ? 'Revealing...' : revealed ? 'Hide' : 'Reveal'}
          </button>
          {isOperator && (
            <button
              className="p-1.5 rounded-btn hover:bg-red-50 transition-colors"
              style={{ color: '#B34A3A' }}
              onClick={() => onDelete(entry.id)}
              data-testid={`vault-delete-${entry.id}`}
            >
              <Trash2 size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Revealed content */}
      {revealed && (
        <div className="mt-3 space-y-2 p-3 rounded-btn" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
          {revealed.username && (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold" style={{ color: 'var(--muted)' }}>Username</p>
                <p className="text-sm font-mono" style={{ color: 'var(--ink)' }}>{revealed.username}</p>
              </div>
              <button onClick={() => copyToClipboard(revealed.username, 'Username')} className="p-1 rounded" style={{ color: 'var(--muted)' }}><Copy size={13} /></button>
            </div>
          )}
          {revealed.password && (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold" style={{ color: 'var(--muted)' }}>Password</p>
                <p className="text-sm font-mono" style={{ color: 'var(--ink)' }}>{revealed.password}</p>
              </div>
              <button onClick={() => copyToClipboard(revealed.password, 'Password')} className="p-1 rounded" style={{ color: 'var(--muted)' }}><Copy size={13} /></button>
            </div>
          )}
          {revealed.value && (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold" style={{ color: 'var(--muted)' }}>Value</p>
                <p className="text-sm font-mono break-all" style={{ color: 'var(--ink)' }}>{revealed.value}</p>
              </div>
              <button onClick={() => copyToClipboard(revealed.value, 'Value')} className="p-1 rounded" style={{ color: 'var(--muted)' }}><Copy size={13} /></button>
            </div>
          )}
          {revealed.notes && (
            <div>
              <p className="text-xs font-semibold" style={{ color: 'var(--muted)' }}>Notes</p>
              <p className="text-sm" style={{ color: 'var(--ink)' }}>{revealed.notes}</p>
            </div>
          )}
          <p className="text-xs" style={{ color: 'var(--muted)' }}>Auto-hides in 30 seconds. Clipboard cleared automatically.</p>
        </div>
      )}
    </div>
  );
}

export default function VaultTab({ projectId, isOperator }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'credential', username: '', password: '', url: '', notes: '', value: '', scope_notes: '' });

  useEffect(() => {
    vaultApi.list(projectId)
      .then(r => setEntries(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [projectId]);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      const r = await vaultApi.create(projectId, form);
      setEntries(prev => [...prev, r.data]);
      setShowAdd(false);
      setForm({ name: '', type: 'credential', username: '', password: '', url: '', notes: '', value: '', scope_notes: '' });
      toast.success('Entry added to vault');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleReveal = async (entryId) => {
    const r = await vaultApi.reveal(projectId, entryId);
    return r.data;
  };

  const handleDelete = async (entryId) => {
    try {
      await vaultApi.delete(projectId, entryId);
      setEntries(prev => prev.filter(e => e.id !== entryId));
      toast.success('Entry removed');
    } catch { toast.error('Failed to delete'); }
  };

  const needsValue = ['api_key', 'secret', 'ssh_key', 'oauth_token'].includes(form.type);

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-bold text-sm" style={{ color: 'var(--ink)' }}>Vault</h3>
          <p className="text-xs" style={{ color: 'var(--muted)' }}>AES-256 encrypted · Access logged · Auto-clears clipboard</p>
        </div>
        <button
          className="btn-primary text-xs flex items-center gap-1.5 px-3 py-1.5"
          onClick={() => setShowAdd(true)}
          data-testid="add-vault-entry"
        >
          <Plus size={13} /> Add Entry
        </button>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[1, 2].map(i => <div key={i} className="h-16 rounded-card animate-pulse" style={{ background: 'var(--border)' }} />)}
        </div>
      ) : entries.length === 0 ? (
        <div className="card-base p-8 text-center">
          <Lock size={32} style={{ color: 'var(--border)', margin: '0 auto 12px' }} />
          <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>No vault entries</p>
          <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>Add credentials, API keys, and secrets here</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="vault-entries-list">
          {entries.map(e => (
            <VaultEntry key={e.id} entry={e} onReveal={handleReveal} onDelete={handleDelete} isOperator={isOperator} />
          ))}
        </div>
      )}

      {/* Add modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="card-base w-full max-w-md p-5 animate-fade-in overflow-y-auto max-h-[90vh]" data-testid="add-vault-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold" style={{ color: 'var(--ink)' }}>Add Vault Entry</h3>
              <button onClick={() => setShowAdd(false)} style={{ color: 'var(--muted)' }}><X size={16} /></button>
            </div>
            <form onSubmit={handleAdd} className="space-y-3">
              <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Name *</label><input className="input-agency text-sm" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="vault-name-input" /></div>
              <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Type</label>
                <select className="input-agency text-sm" value={form.type} onChange={e => setForm({...form, type: e.target.value})} data-testid="vault-type-select">
                  <option value="credential">Credential (username + password)</option>
                  <option value="api_key">API Key</option>
                  <option value="secret">Secret</option>
                  <option value="ssh_key">SSH Key</option>
                  <option value="oauth_token">OAuth Token</option>
                </select>
              </div>
              {form.type === 'credential' && (
                <>
                  <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Username</label><input className="input-agency text-sm" value={form.username} onChange={e => setForm({...form, username: e.target.value})} /></div>
                  <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Password</label><input className="input-agency text-sm font-mono" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} /></div>
                </>
              )}
              {needsValue && (
                <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Value *</label><textarea className="input-agency text-sm font-mono resize-none" rows={3} required value={form.value} onChange={e => setForm({...form, value: e.target.value})} /></div>
              )}
              <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>URL</label><input className="input-agency text-sm" type="url" placeholder="https://" value={form.url} onChange={e => setForm({...form, url: e.target.value})} /></div>
              <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Notes</label><textarea className="input-agency text-sm resize-none" rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
              <div className="flex gap-3"><button type="button" className="flex-1 py-2 text-sm rounded-btn border font-medium" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }} onClick={() => setShowAdd(false)}>Cancel</button><button type="submit" className="flex-1 btn-primary py-2 text-sm" data-testid="vault-submit-button">Add to Vault</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
